import { Button } from "@/components/Primitives/Button"
import { Text } from "@/components/Primitives/Text"
import { ArrowRight } from "lucide-react"
import { Icon } from "@/components/Primitives/Icon"
import { useConfig } from "@/hooks/useConfig"
import { useState, useEffect, useMemo, useRef } from "react"
import { QuestLayout } from "@/components/Complex/QuestLayout"
import confetti from "canvas-confetti"
import { useNavigate } from "react-router-dom"
import { useQuestsStore } from "@/lib/store/quests"
import { MarkdownRenderer } from "@/components/Primitives/MarkdownRenderer"
import { Spinner } from "@/components/Primitives/Spinner"

declare global {
  interface Window {
    lottie: any
  }
}

function LottieAnimation() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    if (window.lottie) {
      setIsLoaded(true)
      return
    }

    const existingScript = document.querySelector('script[src*="lottie-web"]')
    if (existingScript) return

    const script = document.createElement("script")
    script.src = "https://assets.pandorasway.com/lottie/lottie-web.5.13.min.js"
    script.async = true
    script.onload = () => setIsLoaded(true)

    document.head.appendChild(script)
  }, [])

  useEffect(() => {
    if (!isLoaded || !containerRef.current) return

    const animation = window.lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop: false,
      autoplay: true,
      path: "/lottie/quest-complete-trophy.json",
    })

    return () => animation?.destroy()
  }, [isLoaded])

  if (!isLoaded) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  return <div ref={containerRef} className="w-full h-full" />
}

interface QuestCompleteProps {
  onNextModule?: () => void
  questName?: string
  questId?: string
  initialAccordionOpen?: boolean
  stages?: any[]
  conclusionText: string
}

export function QuestComplete({
  questName = "Quest",
  questId,
  initialAccordionOpen = false,
  stages = [],
  conclusionText,
}: QuestCompleteProps) {
  const { t, tp } = useConfig()
  const navigate = useNavigate()
  const [accordionValue, setAccordionValue] = useState<string>(
    initialAccordionOpen ? "quest-progress" : "",
  )

  const completionTitle = tp("config_quest_completion_title", {
    quest_name: questName,
  })

  useEffect(() => {
    const duration = 3 * 1000
    const animationEnd = Date.now() + duration

    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min
    }

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now()

      if (timeLeft <= 0) {
        return clearInterval(interval)
      }

      const particleCount = 50 * (timeLeft / duration)

      confetti({
        particleCount,
        spread: 70,
        origin: { y: 0.3, x: randomInRange(0.2, 0.8) },
      })
    }, 250)

    return () => clearInterval(interval)
  }, [])

  const handleNextModule = () => {
    navigate("/quests")
  }

  const storeQuest = useQuestsStore((state) => state.currentJourney)
  const questStages = useMemo(() => {
    return stages.length > 0 ? stages : storeQuest?.journey_stages || []
  }, [stages, storeQuest])

  const completedStageIds = useMemo(() => {
    const completedIds = new Set<string>()
    if (!questStages) return completedIds

    for (const stage of questStages) {
      completedIds.add(stage.id)
    }

    return completedIds
  }, [questStages])

  const handleStageNavigable = () => true

  const handleStageSelect = (index: number) => {
    if (!questStages || !questStages[index]) {
      return
    }

    setAccordionValue("")

    if (questId) {
      const selectedStage = questStages[index]
      const selectedStageId = selectedStage.id

      navigate(`/quests/${questId}?view=details&stageId=${selectedStageId}`)
    }
  }

  return (
    <QuestLayout
      questId={questId}
      questName={questName}
      isQuestComplete={true}
      stages={questStages}
      currentStageIndex={questStages.length - 1}
      completedStages={completedStageIds}
      isStageNavigable={handleStageNavigable}
      accordionValue={accordionValue}
      setAccordionValue={setAccordionValue}
      onStageSelect={handleStageSelect}
      journeyProgress={100}
    >
      <div className="flex flex-col items-center justify-center py-8 space-y-8 bg-purple-500 -m-4 -mb-6 p-6 min-h-[calc(100vh-170px)] rounded-b-lg">
        <div className="flex items-center justify-center">
          <div className="relative">
            <div className="h-[10rem] w-[10rem] md:h-[12.5rem] md:w-[12.5rem] rounded-full overflow-hidden">
              <LottieAnimation />
            </div>
          </div>
        </div>

        <div className="text-center space-y-4 max-w-md text-neutral-50">
          <Text variant="small" bold className="text-neutral-50 mb-2">
            {completionTitle}
          </Text>

          <Text variant="h4" bold className="text-neutral-50">
            GREAT!
          </Text>

          <MarkdownRenderer
            content={conclusionText}
            className="text-neutral-50"
          />
        </div>

        <Button
          onClick={handleNextModule}
          size="lg"
          className="bg-purple-50 hover:bg-purple-50 text-purple-700 py-2 px-4 font-semibold"
        >
          {t("config_button_quest_next")}
          <Icon
            icon={ArrowRight}
            size="lg"
            className="text-purple-700 w-4 h-4"
          />
        </Button>
      </div>
    </QuestLayout>
  )
}
